/**
 * P1-08 · Variant matrix generation & non-destructive merge (pure).
 *
 * Options × values → the intended combination set, diffed against existing
 * variants keyed by their option-value combo. Regeneration only ever *creates*
 * missing combos and *flags* orphans (combo no longer valid) — it never deletes,
 * so per-variant edits survive adding a colour and orphan removal is an explicit,
 * confirmed step (P1-DB02). All `template:core`: option/value names are data.
 */

/** Option-builder cap — beyond 3 axes the matrix stops being editable (P1-DB02). */
export const MAX_OPTIONS = 3;

/** Above this many rows the grid virtualises and shows a large-matrix notice. */
export const MATRIX_WARN_ROWS = 200;

/** Hard cap — generation refuses to create more combinations than this. */
export const MAX_COMBINATIONS = 2000;

/**
 * Cartesian product in option-position-major order, so ["Red","Blue"]×["S","M"]
 * yields Red/S, Red/M, Blue/S, Blue/M — the natural reading order of the grid.
 * Zero options → one empty combo (the implied single default variant).
 */
export function cartesian<T>(axes: readonly (readonly T[])[]): T[][] {
  let combos: T[][] = [[]];
  for (const axis of axes) {
    combos = combos.flatMap((combo) => axis.map((value) => [...combo, value]));
  }
  return combos;
}

/** Number of combinations without materialising them (for the hard cap). */
export function combinationCount(valueCounts: readonly number[]): number {
  return valueCounts.reduce((n, c) => n * c, 1);
}

/**
 * Canonical merge key for a combo of option-value ids. Sorted so the key is
 * stable regardless of option order changes (reordering options must not orphan
 * every variant).
 */
export function comboKey(valueIds: readonly string[]): string {
  return [...valueIds].sort().join("|");
}

export interface MatrixVariantRef {
  id: string;
  /** The variant's linked option-value ids (its cell in the matrix). */
  optionValueIds: readonly string[];
}

export interface MatrixPlan<T extends MatrixVariantRef> {
  /** Intended combos with no existing variant — to be created. */
  toCreate: string[][];
  /** Existing variants whose combo is still intended, in combo order. */
  survivors: T[];
  /** Existing variants whose combo is no longer valid — flagged, never auto-deleted. */
  orphans: T[];
  /** Total intended combination count. */
  intendedCount: number;
}

/**
 * Diff the intended combination set (cartesian of `optionValueIds` per option,
 * in position order) against the existing variants. A variant survives iff its
 * value set matches an intended combo exactly; everything else — including
 * combo-less variants once options exist, and stale combos after a value was
 * removed — is an orphan awaiting explicit confirmation.
 */
export function planMatrix<T extends MatrixVariantRef>(
  optionValueIds: readonly (readonly string[])[],
  existing: readonly T[],
): MatrixPlan<T> {
  const intended = cartesian(optionValueIds);
  const byKey = new Map(intended.map((combo, i) => [comboKey(combo), i]));

  const survivorsByIndex = new Map<number, T>();
  const orphans: T[] = [];
  for (const variant of existing) {
    const key = comboKey(variant.optionValueIds);
    const index =
      variant.optionValueIds.length === optionValueIds.length
        ? byKey.get(key)
        : undefined;
    // First match wins; a duplicate variant on the same combo is an orphan.
    if (index !== undefined && !survivorsByIndex.has(index)) {
      survivorsByIndex.set(index, variant);
    } else {
      orphans.push(variant);
    }
  }

  const toCreate = intended.filter((_, i) => !survivorsByIndex.has(i));
  const survivors = [...survivorsByIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, v]) => v);

  return { toCreate, survivors, orphans, intendedCount: intended.length };
}
